import React from "react";
import { Box, Typography } from "@mui/material";

/**
 * Renders a visual flash card based on cardType.
 * Returns null if no visual type; caller should fall back to default card.
 */
function renderVisualCard(card, options = {}) {
  const { compact = false, onClick } = options;

  const cardStyle = (border = "#2563eb") => ({
    height: compact ? 400 : 440,
    minHeight: compact ? 400 : 440,
    p: compact ? 2.5 : 3,
    borderRadius: 4,
    border: `5px solid ${border}`,
    bgcolor: "#ffffff",
    boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 1.5,
    textAlign: "center",
    cursor: onClick ? "pointer" : "default",
    transition: "transform 0.2s, box-shadow 0.2s",
    ...(onClick && {
      "&:hover": { transform: "scale(1.02)", boxShadow: "0 6px 20px rgba(0,0,0,0.12)" },
    }),
  });

  const titleEl = (
    <Typography fontWeight={800} sx={{ color: "#1e293b", textTransform: "uppercase", fontSize: compact ? "1.1rem" : "1.4rem", mb: 2.5, letterSpacing: 0.5 }}>
      {card.title}
    </Typography>
  );

  const qaEl = (q, a) => (
    <>
      <Typography sx={{ color: "#475569", fontSize: compact ? "0.9rem" : "1rem", mb: 1 }}>{q}</Typography>
      <Typography fontWeight={800} sx={{ color: "#1e293b", fontSize: compact ? "1.1rem" : "1.25rem" }}>→ {a}</Typography>
    </>
  );

  // Place Value (complex, kept first)
  if (card.cardType === "place-value" && card.placeValue) {
    const pv = card.placeValue;
    const hasHundreds = (pv.hundreds ?? 0) > 0;
    const total = (pv.hundreds ?? 0) * 100 + pv.tens * 10 + pv.ones;
    const cell = compact ? 2 : 2.5;
    const hundredsBlocks = hasHundreds && Array(pv.hundreds).fill(null).map((_, blockIdx) => (
      <Box key={blockIdx} sx={{ display: "flex", flexDirection: "column", gap: 0.05, alignItems: "center", mb: 0.2 }}>
        {Array(10).fill(null).map((_, row) => (
          <Box key={row} sx={{ display: "flex", gap: 0.05 }}>
            {Array(10).fill(null).map((_, col) => (
              <Box key={col} sx={{ width: cell, height: cell, bgcolor: "#059669", borderRadius: 0.2 }} />
            ))}
          </Box>
        ))}
      </Box>
    ));
    const tw = compact ? 5 : 6;
    const th = compact ? 6 : 8;
    const tensBlocks = Array(pv.tens).fill(null).map((_, i) => (
      <Box key={i} sx={{ display: "flex", gap: 0.15, justifyContent: "center", mb: 0.3 }}>
        {Array(10).fill(null).map((_, j) => (
          <Box key={j} sx={{ width: tw, height: th, bgcolor: "#2563eb", borderRadius: 0.3 }} />
        ))}
      </Box>
    ));
    const ow = compact ? 6 : 8;
    const onesBlocks = (
      <Box sx={{ display: "flex", gap: 0.15, justifyContent: "center", flexWrap: "wrap" }}>
        {Array(pv.ones).fill(null).map((_, i) => (
          <Box key={i} sx={{ width: ow, height: ow, bgcolor: "#dc2626", borderRadius: 0.3 }} />
        ))}
      </Box>
    );
    return (
      <Box sx={cardStyle("#2563eb")} onClick={onClick}>
        {titleEl}
        <Typography sx={{ color: "#1e293b", fontSize: "1.25rem", fontWeight: 800, mb: 1.5 }}>Number: {total}</Typography>
        <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "nowrap", justifyContent: "center", alignItems: "flex-start" }}>
          {hasHundreds && (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 48, flexShrink: 0 }}>
              <Box sx={{ width: compact ? 36 : 48, height: compact ? 36 : 48, borderRadius: 2, bgcolor: "#059669", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: compact ? "1.2rem" : "1.5rem", fontWeight: 800, mb: 0.5 }}>{pv.hundreds}</Box>
              <Typography sx={{ color: "#059669", fontSize: compact ? "0.6rem" : "0.7rem", fontWeight: 700, mb: 0.5 }}>Hundreds (100)</Typography>
              {hundredsBlocks}
            </Box>
          )}
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 48, flexShrink: 0 }}>
            <Box sx={{ width: compact ? 36 : 48, height: compact ? 36 : 48, borderRadius: 2, bgcolor: "#2563eb", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: compact ? "1.2rem" : "1.5rem", fontWeight: 800, mb: 0.5 }}>{pv.tens}</Box>
            <Typography sx={{ color: "#2563eb", fontSize: compact ? "0.6rem" : "0.7rem", fontWeight: 700, mb: 0.5 }}>Tens (10)</Typography>
            {tensBlocks}
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 48, flexShrink: 0 }}>
            <Box sx={{ width: compact ? 36 : 48, height: compact ? 36 : 48, borderRadius: 2, bgcolor: "#dc2626", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: compact ? "1.2rem" : "1.5rem", fontWeight: 800, mb: 0.5 }}>{pv.ones}</Box>
            <Typography sx={{ color: "#dc2626", fontSize: compact ? "0.6rem" : "0.7rem", fontWeight: 700, mb: 0.5 }}>Ones (1)</Typography>
            {onesBlocks}
          </Box>
        </Box>
        {qaEl(pv.question, pv.answer)}
      </Box>
    );
  }

  // Number line: visual line with marked value, how to read, how to draw
  if (card.cardType === "number-line" && card.numberLine) {
    const nl = card.numberLine;
    const min = nl.min ?? 0;
    const max = nl.max ?? 10;
    const marked = nl.markedValue ?? min;
    const range = max - min;
    const markedPercent = range === 0 ? 50 : ((marked - min) / range) * 100;
    const ticks = [];
    for (let i = min; i <= max; i++) ticks.push(i);
    const lineWidth = compact ? 200 : 260;
    return (
      <Box sx={cardStyle("#2563eb")} onClick={onClick}>
        {titleEl}
        <Typography sx={{ color: "#475569", fontSize: compact ? "0.85rem" : "0.95rem", mb: 1.5 }}>
          Number line from {min} to {max}
        </Typography>
        <Box sx={{ width: lineWidth, mx: "auto", mb: 2, position: "relative", minHeight: 52 }}>
          {/* Horizontal line */}
          <Box sx={{ position: "absolute", left: 0, right: 0, top: 20, height: 2, bgcolor: "#1e293b", borderRadius: 1 }} />
          {/* Dot above marked value */}
          <Box
            sx={{
              position: "absolute",
              left: `${markedPercent}%`,
              top: 4,
              transform: "translateX(-50%)",
              width: 14,
              height: 14,
              borderRadius: "50%",
              bgcolor: "#dc2626",
              border: "2px solid #991b1b",
              zIndex: 1,
            }}
          />
          {/* Tick marks and labels - positioned by percentage */}
          {ticks.map((n) => {
            const pct = range === 0 ? 50 : ((n - min) / range) * 100;
            return (
              <Box
                key={n}
                sx={{
                  position: "absolute",
                  left: `${pct}%`,
                  transform: "translateX(-50%)",
                  top: 22,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Box sx={{ width: 2, height: 8, bgcolor: "#475569", borderRadius: 0.5 }} />
                <Typography sx={{ fontSize: compact ? "0.8rem" : "0.9rem", fontWeight: 700, color: "#1e293b", mt: 0.25 }}>
                  {n}
                </Typography>
              </Box>
            );
          })}
        </Box>
        <Typography sx={{ color: "#475569", fontSize: compact ? "0.8rem" : "0.9rem", fontWeight: 600 }}>
          How to read: The dot shows the value <Box component="span" sx={{ color: "#dc2626", fontWeight: 800 }}>{marked}</Box>.
        </Typography>
        <Typography sx={{ color: "#374151", fontSize: compact ? "0.75rem" : "0.85rem", mt: 0.5 }}>
          {nl.readTip}
        </Typography>
      </Box>
    );
  }

  // Illustrate sums on a number line (0–10 blank or with start/end dots) — same size as main number-line card
  // Card view (compact) shows only first 4 items; popup uses ws.items (mergedCard already has correct page)
  if (card.cardType === "number-line-sums-worksheet" && card.numberLineSumsWorksheet) {
    const ws = card.numberLineSumsWorksheet;
    const displayItems = compact ? (card.examples?.[0]?.numberLineSumsWorksheet?.items ?? ws.items.slice(0, 4)) : ws.items;
    const lineWidth = compact ? 200 : 260;
    const lineTop = compact ? 14 : 20;
    const tickTop = compact ? 16 : 22;
    const minLineHeight = compact ? 36 : 52;
    const tickHeight = compact ? 6 : 8;
    const dotSize = compact ? 10 : 14;
    const renderMiniNumberLine = (startDot, endDot) => (
      <Box sx={{ width: lineWidth, position: "relative", minHeight: minLineHeight, mb: compact ? 0.5 : 1 }}>
        <Box sx={{ position: "absolute", left: 0, right: 0, top: lineTop, height: 2, bgcolor: "#1e293b", borderRadius: 1 }} />
        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => {
          const pct = (n / 10) * 100;
          const showStart = startDot !== undefined && n === startDot;
          const showEnd = endDot !== undefined && n === endDot;
          return (
            <Box key={n} sx={{ position: "absolute", left: `${pct}%`, transform: "translateX(-50%)", top: tickTop, display: "flex", flexDirection: "column", alignItems: "center" }}>
              <Box sx={{ width: 2, height: tickHeight, bgcolor: "#475569", borderRadius: 0.5 }} />
              {(showStart || showEnd) && (
                <Box sx={{ position: "absolute", top: compact ? -12 : -16, width: dotSize, height: dotSize, borderRadius: "50%", bgcolor: showEnd ? "#dc2626" : "#2563eb", border: "2px solid", borderColor: showEnd ? "#991b1b" : "#1d4ed8", zIndex: 1 }} />
              )}
              <Typography sx={{ fontSize: compact ? "0.75rem" : "0.9rem", fontWeight: 700, color: "#1e293b", mt: 0.25 }}>{n}</Typography>
            </Box>
          );
        })}
      </Box>
    );
    const worksheetCardStyle = { ...cardStyle("#2563eb"), justifyContent: "flex-start", overflow: "visible", height: compact ? 400 : "auto", minHeight: compact ? 400 : 360, p: compact ? 2 : 2.5, gap: 0.5 };
    const itemsContainerSx = { width: "100%", textAlign: "left" };
    if (!compact && displayItems.length < 4) {
      itemsContainerSx.minHeight = 280;
    }
    return (
      <Box sx={worksheetCardStyle} onClick={onClick}>
        <Typography fontWeight={800} sx={{ color: "#1e293b", textTransform: "uppercase", fontSize: compact ? "0.85rem" : "1rem", mb: 0.75, letterSpacing: 0.5 }}>{card.title}</Typography>
        <Typography sx={{ color: "#475569", fontSize: compact ? "0.75rem" : "0.85rem", mb: 1, textAlign: "left", width: "100%" }}>{ws.instruction}</Typography>
        <Box sx={itemsContainerSx}>
          {displayItems.map((item, idx) => (
            <Box key={idx} sx={{ mb: compact ? 0.5 : 1.25 }}>
              <Typography sx={{ color: "#1e293b", fontSize: compact ? "0.9rem" : "1rem", fontWeight: 600 }}>{item.label}. {item.sum}</Typography>
              {renderMiniNumberLine()}
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  // Place value worksheet: one card with instruction + a,b,c,d and answers
  if (card.cardType === "place-value-worksheet" && card.placeValueWorksheet) {
    const ws = card.placeValueWorksheet;
    const worksheetCardStyle = { ...cardStyle("#2563eb"), justifyContent: "flex-start", overflow: "visible", pt: compact ? 3 : 3.5 };
    const renderNumberWithUnderline = (numStr, underlinedDigit) => {
      const chars = numStr.split("");
      return (
        <Typography component="span" sx={{ fontFamily: "monospace", fontSize: compact ? "1.1rem" : "1.25rem", fontWeight: 700 }}>
          {chars.map((ch, i) =>
            ch === underlinedDigit ? (
              <Box key={i} component="span" sx={{ textDecoration: "underline", textUnderlineOffset: 2 }}>{ch}</Box>
            ) : (
              <span key={i}>{ch}</span>
            )
          )}
        </Typography>
      );
    };
    return (
      <Box sx={worksheetCardStyle} onClick={onClick}>
        {titleEl}
        <Box sx={{ width: "100%", textAlign: "left" }}>
          {ws.items.map((item, idx) => (
            <Box key={idx} sx={{ mb: 1.5, display: "flex", flexDirection: "column", gap: 0.25 }}>
              <Typography sx={{ color: "#1e293b", fontSize: compact ? "1rem" : "1.1rem" }}>
                {item.label}) {renderNumberWithUnderline(item.number, item.underlinedDigit)}
              </Typography>
              <Typography sx={{ color: "#374151", fontSize: compact ? "0.9rem" : "1rem" }}>
                Answer: <Box component="span" sx={{ borderBottom: "1px solid #94a3b8", minWidth: 48, display: "inline-block" }} />
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  // Numbers in words worksheet: one card with instruction + a–e numbers and answer lines (tight layout to fit all)
  if (card.cardType === "numbers-in-words-worksheet" && card.numbersInWordsWorksheet) {
    const ws = card.numbersInWordsWorksheet;
    const worksheetCardStyle = {
      ...cardStyle("#2563eb"),
      justifyContent: "flex-start",
      overflow: "visible",
      height: "auto",
      minHeight: compact ? 400 : 440,
      p: compact ? 2 : 2.5,
      gap: 0.75,
    };
    return (
      <Box sx={worksheetCardStyle} onClick={onClick}>
        <Typography fontWeight={800} sx={{ color: "#1e293b", textTransform: "uppercase", fontSize: compact ? "1rem" : "1.2rem", mb: 1.25, letterSpacing: 0.5 }}>
          {card.title}
        </Typography>
        <Box sx={{ width: "100%", textAlign: "left" }}>
          {ws.items.map((item, idx) => (
            <Box key={idx} sx={{ mb: idx < ws.items.length - 1 ? 0.75 : 0, display: "flex", flexDirection: "column", gap: 0.15 }}>
              <Typography sx={{ color: "#1e293b", fontSize: compact ? "0.95rem" : "1.05rem", fontWeight: 600 }}>
                {item.label}) {item.number}
              </Typography>
              <Typography sx={{ color: "#374151", fontSize: compact ? "0.85rem" : "0.95rem" }}>
                Answer: <Box component="span" sx={{ borderBottom: "1px solid #94a3b8", minWidth: 100, display: "inline-block" }} />
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  // Ascending order worksheet: arrange numbers smallest to largest (tight layout)
  if (card.cardType === "ascending-order-worksheet" && card.ascendingOrderWorksheet) {
    const ws = card.ascendingOrderWorksheet;
    const worksheetCardStyle = {
      ...cardStyle("#2563eb"),
      justifyContent: "flex-start",
      overflow: "visible",
      height: "auto",
      minHeight: compact ? 400 : 440,
      p: compact ? 2 : 2.5,
      gap: 0.75,
    };
    return (
      <Box sx={worksheetCardStyle} onClick={onClick}>
        <Typography fontWeight={800} sx={{ color: "#1e293b", textTransform: "uppercase", fontSize: compact ? "0.95rem" : "1.1rem", mb: 1.25, letterSpacing: 0.5 }}>
          {card.title}
        </Typography>
        <Box sx={{ width: "100%", textAlign: "left" }}>
          {ws.items.map((item, idx) => (
            <Box key={idx} sx={{ mb: idx < ws.items.length - 1 ? 1 : 0, display: "flex", flexDirection: "column", gap: 0.15 }}>
              <Typography sx={{ color: "#1e293b", fontSize: compact ? "0.95rem" : "1.05rem", fontWeight: 600 }}>
                {item.label}) {item.numbers}
              </Typography>
              <Typography sx={{ color: "#374151", fontSize: compact ? "0.85rem" : "0.95rem" }}>
                Answer: <Box component="span" sx={{ borderBottom: "1px solid #94a3b8", minWidth: 180, display: "inline-block" }} />
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  // Descending order worksheet: arrange numbers largest to smallest (tight layout)
  if (card.cardType === "descending-order-worksheet" && card.descendingOrderWorksheet) {
    const ws = card.descendingOrderWorksheet;
    const worksheetCardStyle = {
      ...cardStyle("#2563eb"),
      justifyContent: "flex-start",
      overflow: "visible",
      height: "auto",
      minHeight: compact ? 400 : 440,
      p: compact ? 2 : 2.5,
      gap: 0.75,
    };
    return (
      <Box sx={worksheetCardStyle} onClick={onClick}>
        <Typography fontWeight={800} sx={{ color: "#1e293b", textTransform: "uppercase", fontSize: compact ? "0.95rem" : "1.1rem", mb: 1.25, letterSpacing: 0.5 }}>
          {card.title}
        </Typography>
        <Box sx={{ width: "100%", textAlign: "left" }}>
          {ws.items.map((item, idx) => (
            <Box key={idx} sx={{ mb: idx < ws.items.length - 1 ? 1 : 0, display: "flex", flexDirection: "column", gap: 0.15 }}>
              <Typography sx={{ color: "#1e293b", fontSize: compact ? "0.95rem" : "1.05rem", fontWeight: 600 }}>
                {item.label}) {item.numbers}
              </Typography>
              <Typography sx={{ color: "#374151", fontSize: compact ? "0.85rem" : "0.95rem" }}>
                Answer: <Box component="span" sx={{ borderBottom: "1px solid #94a3b8", minWidth: 180, display: "inline-block" }} />
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  // Comparison symbol worksheet: <, >, = (tight layout for 5 items)
  if (card.cardType === "comparison-symbol-worksheet" && card.comparisonSymbolWorksheet) {
    const ws = card.comparisonSymbolWorksheet;
    const worksheetCardStyle = {
      ...cardStyle("#2563eb"),
      justifyContent: "flex-start",
      overflow: "visible",
      height: "auto",
      minHeight: compact ? 400 : 440,
      p: compact ? 2 : 2.5,
      gap: 0.75,
    };
    return (
      <Box sx={worksheetCardStyle} onClick={onClick}>
        <Typography fontWeight={800} sx={{ color: "#1e293b", textTransform: "uppercase", fontSize: compact ? "0.9rem" : "1rem", mb: 1.25, letterSpacing: 0.5 }}>
          {card.title}
        </Typography>
        <Box sx={{ width: "100%", textAlign: "left" }}>
          {ws.items.map((item, idx) => (
            <Box key={idx} sx={{ mb: idx < ws.items.length - 1 ? 0.75 : 0, display: "flex", flexDirection: "column", gap: 0.15 }}>
              <Typography sx={{ color: "#1e293b", fontSize: compact ? "0.9rem" : "1rem", fontWeight: 600 }}>
                {item.label}) {item.left}
                <Box component="span" sx={{ borderBottom: "1px solid #94a3b8", minWidth: 28, mx: 0.5, display: "inline-block", verticalAlign: "middle" }} />
                {item.right}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  // Round to nearest 10 worksheet (tight layout for 6 items)
  if (card.cardType === "round-to-nearest-worksheet" && card.roundToNearestWorksheet) {
    const ws = card.roundToNearestWorksheet;
    const worksheetCardStyle = {
      ...cardStyle("#2563eb"),
      justifyContent: "flex-start",
      overflow: "visible",
      height: "auto",
      minHeight: compact ? 400 : 440,
      p: compact ? 2 : 2.5,
      gap: 0.75,
    };
    return (
      <Box sx={worksheetCardStyle} onClick={onClick}>
        <Typography fontWeight={800} sx={{ color: "#1e293b", textTransform: "uppercase", fontSize: compact ? "0.95rem" : "1.1rem", mb: 1.25, letterSpacing: 0.5 }}>
          {card.title}
        </Typography>
        <Box sx={{ width: "100%", textAlign: "left" }}>
          {ws.items.map((item, idx) => (
            <Box key={idx} sx={{ mb: idx < ws.items.length - 1 ? 0.75 : 0, display: "flex", flexDirection: "column", gap: 0.15 }}>
              <Typography sx={{ color: "#1e293b", fontSize: compact ? "0.95rem" : "1.05rem", fontWeight: 600 }}>
                {item.label}) {item.number}
              </Typography>
              <Typography sx={{ color: "#374151", fontSize: compact ? "0.85rem" : "0.95rem" }}>
                Answer: <Box component="span" sx={{ borderBottom: "1px solid #94a3b8", minWidth: 60, display: "inline-block" }} />
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  // Round to nearest 100 worksheet (tight layout for 6 items)
  if (card.cardType === "round-to-nearest-hundred-worksheet" && card.roundToNearestHundredWorksheet) {
    const ws = card.roundToNearestHundredWorksheet;
    const worksheetCardStyle = {
      ...cardStyle("#2563eb"),
      justifyContent: "flex-start",
      overflow: "visible",
      height: "auto",
      minHeight: compact ? 400 : 440,
      p: compact ? 2 : 2.5,
      gap: 0.75,
    };
    return (
      <Box sx={worksheetCardStyle} onClick={onClick}>
        <Typography fontWeight={800} sx={{ color: "#1e293b", textTransform: "uppercase", fontSize: compact ? "0.95rem" : "1.1rem", mb: 1.25, letterSpacing: 0.5 }}>
          {card.title}
        </Typography>
        <Box sx={{ width: "100%", textAlign: "left" }}>
          {ws.items.map((item, idx) => (
            <Box key={idx} sx={{ mb: idx < ws.items.length - 1 ? 0.75 : 0, display: "flex", flexDirection: "column", gap: 0.15 }}>
              <Typography sx={{ color: "#1e293b", fontSize: compact ? "0.95rem" : "1.05rem", fontWeight: 600 }}>
                {item.label}) {item.number}
              </Typography>
              <Typography sx={{ color: "#374151", fontSize: compact ? "0.85rem" : "0.95rem" }}>
                Answer: <Box component="span" sx={{ borderBottom: "1px solid #94a3b8", minWidth: 60, display: "inline-block" }} />
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  // Fill in the missing numbers (place value / expanded form)
  if (card.cardType === "fill-missing-numbers-worksheet" && card.fillMissingNumbersWorksheet) {
    const ws = card.fillMissingNumbersWorksheet;
    const worksheetCardStyle = {
      ...cardStyle("#2563eb"),
      justifyContent: "flex-start",
      overflow: "visible",
      height: "auto",
      minHeight: compact ? 400 : 440,
      p: compact ? 2 : 2.5,
      gap: 0.75,
    };
    const renderEquation = (equation) => {
      const parts = equation.split("___");
      return (
        <Typography component="span" sx={{ fontFamily: "monospace", fontSize: compact ? "0.95rem" : "1.05rem", fontWeight: 600 }}>
          {parts[0]}
          <Box component="span" sx={{ display: "inline-block", minWidth: 40, height: 22, border: "2px solid #94a3b8", borderRadius: 0.5, mx: 0.25, verticalAlign: "middle" }} />
          {parts[1]}
        </Typography>
      );
    };
    return (
      <Box sx={worksheetCardStyle} onClick={onClick}>
        <Typography fontWeight={800} sx={{ color: "#1e293b", textTransform: "uppercase", fontSize: compact ? "0.95rem" : "1.1rem", mb: 1.25, letterSpacing: 0.5 }}>
          {card.title}
        </Typography>
        <Box sx={{ width: "100%", textAlign: "left" }}>
          {ws.items.map((item, idx) => (
            <Box key={idx} sx={{ mb: idx < ws.items.length - 1 ? 1 : 0 }}>
              <Typography sx={{ color: "#1e293b", fontSize: compact ? "0.95rem" : "1.05rem", fontWeight: 600 }}>
                {item.label}) {renderEquation(item.equation)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  // Complete the number bonds (equation with one blank)
  if (card.cardType === "number-bonds-worksheet" && card.numberBondsWorksheet) {
    const ws = card.numberBondsWorksheet;
    const worksheetCardStyle = {
      ...cardStyle("#2563eb"),
      justifyContent: "flex-start",
      overflow: "visible",
      height: "auto",
      minHeight: compact ? 400 : 440,
      p: compact ? 2 : 2.5,
      gap: 0.75,
    };
    const renderEquation = (equation) => {
      const parts = equation.split("___");
      return (
        <Typography component="span" sx={{ fontFamily: "monospace", fontSize: compact ? "0.95rem" : "1.05rem", fontWeight: 600 }}>
          {parts[0]}
          <Box component="span" sx={{ display: "inline-block", minWidth: 40, height: 22, border: "2px solid #94a3b8", borderRadius: 0.5, mx: 0.25, verticalAlign: "middle" }} />
          {parts[1]}
        </Typography>
      );
    };
    return (
      <Box sx={worksheetCardStyle} onClick={onClick}>
        <Typography fontWeight={800} sx={{ color: "#1e293b", textTransform: "uppercase", fontSize: compact ? "0.95rem" : "1.1rem", mb: 1.25, letterSpacing: 0.5 }}>
          {card.title}
        </Typography>
        <Box sx={{ width: "100%", textAlign: "left" }}>
          {ws.items.map((item, idx) => (
            <Box key={idx} sx={{ mb: idx < ws.items.length - 1 ? 1.25 : 0 }}>
              <Typography sx={{ color: "#1e293b", fontSize: compact ? "0.95rem" : "1.05rem", fontWeight: 600 }}>
                {item.label}) {renderEquation(item.equation)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  // Write numbers as two pairs of number bonds (with example)
  if (card.cardType === "number-bonds-pairs-worksheet" && card.numberBondsPairsWorksheet) {
    const ws = card.numberBondsPairsWorksheet;
    const worksheetCardStyle = {
      ...cardStyle("#2563eb"),
      justifyContent: "flex-start",
      overflow: "visible",
      height: "auto",
      minHeight: compact ? 400 : 440,
      p: compact ? 2 : 2.5,
      gap: 0.75,
    };
    const blankLine = <Box component="span" sx={{ borderBottom: "1px solid #94a3b8", minWidth: 100, display: "inline-block", mx: 0.5, verticalAlign: "middle" }} />;
    return (
      <Box sx={worksheetCardStyle} onClick={onClick}>
        <Typography fontWeight={800} sx={{ color: "#1e293b", textTransform: "uppercase", fontSize: compact ? "0.9rem" : "1rem", mb: 1, letterSpacing: 0.5 }}>
          {card.title}
        </Typography>
        <Typography sx={{ color: "#475569", fontSize: compact ? "0.8rem" : "0.9rem", mb: 0.75, textAlign: "left", width: "100%" }}>
          Write the numbers below as two separate pairs of number bonds.
        </Typography>
        {ws.example && (
          <Typography sx={{ color: "#374151", fontSize: compact ? "0.85rem" : "0.95rem", mb: 1, fontStyle: "italic" }}>
            Example: {ws.example}
          </Typography>
        )}
        <Box sx={{ width: "100%", textAlign: "left" }}>
          {ws.items.map((item, idx) => (
            <Box key={idx} sx={{ mb: idx < ws.items.length - 1 ? 1 : 0 }}>
              <Typography sx={{ color: "#1e293b", fontSize: compact ? "0.95rem" : "1.05rem", fontWeight: 600 }}>
                {item.label}) {item.number} = {blankLine} = {blankLine}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  // Partition method: complete the sums (addition)
  if (card.cardType === "partition-method-worksheet" && card.partitionMethodWorksheet) {
    const ws = card.partitionMethodWorksheet;
    const worksheetCardStyle = {
      ...cardStyle("#2563eb"),
      justifyContent: "flex-start",
      overflow: "visible",
      height: "auto",
      minHeight: compact ? 400 : 440,
      p: compact ? 2 : 2.5,
      gap: 0.75,
    };
    return (
      <Box sx={worksheetCardStyle} onClick={onClick}>
        <Typography fontWeight={800} sx={{ color: "#1e293b", textTransform: "uppercase", fontSize: compact ? "0.9rem" : "1rem", mb: 1, letterSpacing: 0.5 }}>
          {card.title}
        </Typography>
        {ws.instruction && (
          <Typography sx={{ color: "#475569", fontSize: compact ? "0.8rem" : "0.9rem", mb: 1, textAlign: "left", width: "100%" }}>
            {ws.instruction}
          </Typography>
        )}
        <Box sx={{ width: "100%", textAlign: "left" }}>
          {ws.items.map((item, idx) => (
            <Box key={idx} sx={{ mb: idx < ws.items.length - 1 ? 1 : 0, display: "flex", flexDirection: "column", gap: 0.15 }}>
              <Typography sx={{ color: "#1e293b", fontSize: compact ? "0.95rem" : "1.05rem", fontWeight: 600 }}>
                {item.label}) {item.sum}
              </Typography>
              <Typography sx={{ color: "#374151", fontSize: compact ? "0.85rem" : "0.95rem" }}>
                Answer: <Box component="span" sx={{ borderBottom: "1px solid #94a3b8", minWidth: 80, display: "inline-block" }} />
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  // Arithmetic (styled like Place Value: colored blocks, dotted line, visual aids)
  if (card.cardType === "arithmetic" && card.arithmetic) {
    const ar = card.arithmetic;
    const opSym = ar.op === "add" ? "+" : ar.op === "subtract" ? "−" : ar.op === "multiply" ? "×" : "÷";
    const numBox = (val, bg, color = "#fff") => (
      <Box sx={{ px: 1.5, py: 0.75, borderRadius: 2, bgcolor: bg, color, fontWeight: 800, fontSize: "1.25rem", boxShadow: "0 2px 6px rgba(0,0,0,0.15)" }}>{val}</Box>
    );
    const opBox = (sym) => (
      <Box sx={{ px: 0.75, color: "#2563eb", fontWeight: 800, fontSize: "1.5rem" }}>{sym}</Box>
    );
    // Visual blocks per operation
    const renderVisual = () => {
      if (ar.op === "add") {
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", justifyContent: "center", mb: 1 }}>
            <Box sx={{ display: "flex", gap: 0.15 }}>
              {Array(ar.a).fill(null).map((_, i) => (
                <Box key={i} sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: "#059669", border: "1px solid #047857" }} />
              ))}
            </Box>
            <Box sx={{ display: "flex", gap: 0.2 }}>
              {Array(ar.b).fill(null).map((_, i) => (
                <Box key={i} sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: "#2563eb", border: "1px solid #b91c1c" }} />
              ))}
            </Box>
          </Box>
        );
      }
      if (ar.op === "subtract") {
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap", justifyContent: "center", mb: 2 }}>
            {Array(ar.a).fill(null).map((_, i) => (
              <Box key={i} sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: i < ar.b ? "#94a3b8" : "#2563eb", opacity: i < ar.b ? 0.5 : 1 }} />
            ))}
          </Box>
        );
      }
      if (ar.op === "multiply") {
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3, alignItems: "center", mb: 2 }}>
            {Array(ar.b).fill(null).map((_, row) => (
              <Box key={row} sx={{ display: "flex", gap: 0.3 }}>
                {Array(ar.a).fill(null).map((_, col) => (
                  <Box key={col} sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: "#7c3aed" }} />
                ))}
              </Box>
            ))}
          </Box>
        );
      }
      if (ar.op === "divide") {
        const groups = Math.ceil(ar.a / ar.b);
        return (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center", mb: 2 }}>
            {Array(ar.b).fill(null).map((_, g) => (
              <Box key={g} sx={{ display: "flex", gap: 0.2 }}>
                {Array(groups).fill(null).map((_, i) => (
                  <Box key={i} sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: "#059669" }} />
                ))}
              </Box>
            ))}
          </Box>
        );
      }
      return null;
    };
    return (
      <Box sx={cardStyle("#2563eb")} onClick={onClick}>
        {titleEl}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap", justifyContent: "center", mb: 2 }}>
          {numBox(ar.a, "#059669")}
          {opBox(opSym)}
          {numBox(ar.b, "#2563eb")}
          {opBox("=")}
          {numBox("?", "#ea580c")}
        </Box>
        {renderVisual()}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, mt: 0.5 }}>
          <Typography sx={{ color: "#1e293b", fontWeight: 800 }}>→</Typography>
          <Typography sx={{ color: "#92400e", fontWeight: 800, fontSize: compact ? "1.2rem" : "1.5rem" }}>{ar.answer}</Typography>
        </Box>
      </Box>
    );
  }

  // Fraction
  if (card.cardType === "fraction" && card.fraction) {
    const f = card.fraction;
    return (
      <Box sx={cardStyle("#ec4899")} onClick={onClick}>
        {titleEl}
        <Box sx={{ display: "flex", flexWrap: "nowrap", gap: 0.3, justifyContent: "center", mb: 2 }}>
          {Array(f.den).fill(null).map((_, i) => (
            <Box key={i} sx={{ width: 24, height: 24, borderRadius: 1, bgcolor: i < f.num ? "#ec4899" : "#e5e7eb" }} />
          ))}
        </Box>
        <Typography sx={{ fontSize: "1.2rem", fontWeight: 700, mb: 1 }}>{f.num}/{f.den}</Typography>
        {qaEl(f.question, f.answer)}
      </Box>
    );
  }

  // Decimal
  if (card.cardType === "decimal" && card.decimal) {
    const d = card.decimal;
    return (
      <Box sx={cardStyle()} onClick={onClick}>
        {titleEl}
        <Typography sx={{ fontSize: "2rem", fontWeight: 800, color: "#7c3aed", mb: 2 }}>{d.value}</Typography>
        {qaEl(d.question, d.answer)}
      </Box>
    );
  }

  // Percent
  if (card.cardType === "percent" && card.percent) {
    const p = card.percent;
    const pct = Math.max(0, Math.min(100, Number(p.value) || 0));
    const angle = (pct / 100) * 360;
    return (
      <Box sx={cardStyle()} onClick={onClick}>
        {titleEl}
        <Box
          sx={{
            width: 90,
            height: 90,
            borderRadius: "50%",
            background: `conic-gradient(#059669 0deg ${angle}deg, #e5e7eb ${angle}deg 360deg)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2,
          }}
        >
          <Box
            sx={{
              width: 70,
              height: 70,
              borderRadius: "50%",
              bgcolor: "#ffffff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.5rem",
              fontWeight: 800,
              color: "#059669",
            }}
          >
            {p.value}%
          </Box>
        </Box>
        {qaEl(p.question, p.answer)}
      </Box>
    );
  }

  // Ratio
  if (card.cardType === "ratio" && card.ratio) {
    const r = card.ratio;
    return (
      <Box sx={cardStyle()} onClick={onClick}>
        {titleEl}
        <Box sx={{ display: "flex", gap: 1, mb: 2, alignItems: "center", justifyContent: "center" }}>
          {Array(r.a).fill(null).map((_, i) => <Box key={`a${i}`} sx={{ width: 28, height: 28, borderRadius: 1, bgcolor: "#2563eb" }} />)}
          <Typography fontWeight={800} sx={{ fontSize: "1.2rem" }}>:</Typography>
          {Array(r.b).fill(null).map((_, i) => <Box key={`b${i}`} sx={{ width: 28, height: 28, borderRadius: 1, bgcolor: "#2563eb" }} />)}
        </Box>
        {qaEl(r.question, r.answer)}
      </Box>
    );
  }

  // Proportion
  if (card.cardType === "proportion" && card.proportion) {
    const p = card.proportion;
    return (
      <Box sx={cardStyle()} onClick={onClick}>
        {titleEl}
        <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, mb: 2 }}>{p.r1[0]}:{p.r1[1]} = {p.r2[0]}:{p.r2[1]}</Typography>
        {qaEl(p.question, p.answer)}
      </Box>
    );
  }

  // Angle
  if (card.cardType === "angle" && card.angle) {
    const ag = card.angle;
    const angleStyles = {
      right: { transform: "rotate(0deg)", borderRight: "4px solid #1e293b", borderBottom: "4px solid #1e293b", width: 40, height: 40 },
      acute: { borderLeft: "4px solid #1e293b", borderBottom: "4px solid #1e293b", width: 50, height: 30, transform: "rotate(-20deg)", borderTopLeftRadius: 4 },
      obtuse: { borderLeft: "4px solid #1e293b", borderBottom: "4px solid #1e293b", width: 50, height: 30, transform: "rotate(20deg)", borderTopLeftRadius: 4 },
    };
    return (
      <Box sx={cardStyle()} onClick={onClick}>
        {titleEl}
        <Box sx={{ ...angleStyles[ag.type] || angleStyles.right, mb: 2 }} />
        {qaEl(ag.question, ag.answer)}
      </Box>
    );
  }

  // Perimeter
  if (card.cardType === "perimeter" && card.perimeter) {
    const p = card.perimeter;
    const perim = 2 * (p.w + p.h);
    return (
      <Box sx={cardStyle()} onClick={onClick}>
        {titleEl}
        <Box sx={{ width: p.w * 24, height: p.h * 24, border: "3px solid #059669", borderRadius: 1, mb: 2 }} />
        <Typography sx={{ fontSize: "0.9rem", mb: 1 }}>{p.w} × {p.h} rectangle</Typography>
        {qaEl(p.question, p.answer)}
      </Box>
    );
  }

  // Area
  if (card.cardType === "area" && card.area) {
    const a = card.area;
    return (
      <Box sx={cardStyle()} onClick={onClick}>
        {titleEl}
        <Box sx={{ width: a.w * 24, height: a.h * 24, bgcolor: "#dbeafe", border: "2px solid #2563eb", borderRadius: 1, mb: 2, display: "flex", flexWrap: "wrap" }}>
          {Array(a.w * a.h).fill(null).map((_, i) => <Box key={i} sx={{ width: 22, height: 22, border: "1px solid #93c5fd", boxSizing: "border-box" }} />)}
        </Box>
        {qaEl(a.question, a.answer)}
      </Box>
    );
  }

  // Volume
  if (card.cardType === "volume" && card.volume) {
    const v = card.volume;
    const cubes = v.l * v.w * v.h;
    return (
      <Box sx={cardStyle()} onClick={onClick}>
        {titleEl}
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", maxWidth: 90, justifyContent: "center", mb: 2 }}>
          {Array(Math.min(cubes, 12)).fill(null).map((_, i) => <Box key={i} sx={{ width: 18, height: 18, bgcolor: "#a78bfa", borderRadius: 0.5 }} />)}
        </Box>
        <Typography sx={{ fontSize: "0.85rem", mb: 1 }}>{v.l}×{v.w}×{v.h} blocks</Typography>
        {qaEl(v.question, v.answer)}
      </Box>
    );
  }

  // Equation / Expression
  if ((card.cardType === "equation" && card.equation) || (card.cardType === "expression" && card.expression)) {
    const eq = card.equation || card.expression;
    return (
      <Box sx={cardStyle()} onClick={onClick}>
        {titleEl}
        <Typography sx={{ fontSize: "1.4rem", fontWeight: 700, fontFamily: "monospace", color: "#7c3aed", mb: 2 }}>{eq.expr}</Typography>
        {qaEl(eq.question, eq.answer)}
      </Box>
    );
  }

  // Word pair (synonym/antonym)
  if (card.cardType === "word-pair" && card.wordPair) {
    const w = card.wordPair;
    const arrow = w.type === "synonym" ? "↔" : "⇄";
    return (
      <Box sx={cardStyle()} onClick={onClick}>
        {titleEl}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <Box sx={{ px: 2, py: 1, bgcolor: "#dbeafe", borderRadius: 2, fontWeight: 700 }}>{w.word1}</Box>
          <Typography sx={{ fontSize: "1.5rem" }}>{arrow}</Typography>
          <Box sx={{ px: 2, py: 1, bgcolor: "#dbeafe", borderRadius: 2, fontWeight: 700 }}>{w.word2}</Box>
        </Box>
        {qaEl(w.question, w.answer)}
      </Box>
    );
  }

  // Anagram
  if (card.cardType === "anagram" && card.anagram) {
    const ag = card.anagram;
    return (
      <Box sx={cardStyle()} onClick={onClick}>
        {titleEl}
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", justifyContent: "center", mb: 2 }}>
          {ag.scrambled.split("").map((c, i) => (
            <Box key={i} sx={{ width: 28, height: 32, bgcolor: "#fde047", borderRadius: 1, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800 }}>{c}</Box>
          ))}
        </Box>
        {qaEl(ag.question, ag.answer)}
      </Box>
    );
  }

  // Compound word
  if (card.cardType === "compound" && card.compound) {
    const c = card.compound;
    return (
      <Box sx={cardStyle()} onClick={onClick}>
        {titleEl}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 2 }}>
          <Box sx={{ px: 1.5, py: 0.5, bgcolor: "#dcfce7", borderRadius: 2, fontWeight: 700 }}>{c.part1}</Box>
          <Typography sx={{ fontSize: "1.2rem", fontWeight: 800 }}>+</Typography>
          <Box sx={{ px: 1.5, py: 0.5, bgcolor: "#dcfce7", borderRadius: 2, fontWeight: 700 }}>{c.part2}</Box>
        </Box>
        {qaEl(c.question, c.answer)}
      </Box>
    );
  }

  // Sequence
  if (card.cardType === "sequence" && card.sequence) {
    const s = card.sequence;
    return (
      <Box sx={cardStyle()} onClick={onClick}>
        {titleEl}
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", justifyContent: "center", mb: 2 }}>
          {s.terms.map((t, i) => (
            <Box key={i} sx={{ px: 1.5, py: 0.5, bgcolor: "#e0e7ff", borderRadius: 2, fontWeight: 700 }}>{t}</Box>
          ))}
          <Typography sx={{ fontWeight: 800 }}>→ ?</Typography>
        </Box>
        {qaEl(s.question, s.answer)}
      </Box>
    );
  }

  // Shape pattern
  if (card.cardType === "shape-pattern" && card.shapePattern) {
    const sp = card.shapePattern;
    return (
      <Box sx={cardStyle()} onClick={onClick}>
        {titleEl}
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center", mb: 2, fontSize: "1.5rem" }}>
          {sp.shapes.map((sh, i) => <span key={i}>{sh}</span>)}
          <Typography component="span" sx={{ fontWeight: 800 }}>→ ?</Typography>
        </Box>
        {qaEl(sp.question, sp.answer)}
      </Box>
    );
  }

  // Digit
  if (card.cardType === "digit" && card.digit) {
    const d = card.digit;
    return (
      <Box sx={cardStyle("#7c3aed")} onClick={onClick}>
        {titleEl}
        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
          {d.examples.map((n, i) => (
            <Box key={i} sx={{ width: 40, height: 48, bgcolor: "#e9d5ff", borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.5rem", fontWeight: 800 }}>{n}</Box>
          ))}
        </Box>
        {qaEl(d.question, d.answer)}
      </Box>
    );
  }

  // Rounding
  if (card.cardType === "rounding" && card.rounding) {
    const r = card.rounding;
    return (
      <Box sx={cardStyle("#059669")} onClick={onClick}>
        {titleEl}
        <Typography sx={{ fontSize: "2rem", fontWeight: 800, color: "#059669", mb: 2 }}>{r.number} → ?</Typography>
        {qaEl(r.question, r.answer)}
      </Box>
    );
  }

  // Sharing ratio
  if (card.cardType === "sharing-ratio" && card.sharingRatio) {
    const s = card.sharingRatio;
    return (
      <Box sx={cardStyle()} onClick={onClick}>
        {titleEl}
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", justifyContent: "center", mb: 2 }}>
          {Array(s.total).fill(null).map((_, i) => (
            <Box key={i} sx={{ width: 16, height: 16, borderRadius: 0.5, bgcolor: i < s.ratio[0] ? "#2563eb" : "#2563eb" }} />
          ))}
        </Box>
        <Typography sx={{ fontSize: "0.9rem", mb: 1 }}>Share {s.total} in ratio {s.ratio[0]}:{s.ratio[1]}</Typography>
        {qaEl(s.question, s.answer)}
      </Box>
    );
  }

  // Statistics
  if (card.cardType === "statistics" && card.statistics) {
    const s = card.statistics;
    return (
      <Box sx={cardStyle("#059669")} onClick={onClick}>
        {titleEl}
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", justifyContent: "center", mb: 2 }}>
          {s.values.map((v, i) => (
            <Box key={i} sx={{ px: 1.5, py: 0.5, bgcolor: "#d1fae5", borderRadius: 2, fontWeight: 700 }}>{v}</Box>
          ))}
        </Box>
        {qaEl(s.question, s.answer)}
      </Box>
    );
  }

  // Multi-step
  if (card.cardType === "multi-step" && card.multiStep) {
    const m = card.multiStep;
    return (
      <Box sx={cardStyle()} onClick={onClick}>
        {titleEl}
        {/* Question first, then steps, then final answer */}
        <Typography sx={{ color: "#475569", fontSize: "1rem", mb: 1.5 }}>
          {m.question}
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mb: 2 }}>
          {m.steps.map((step, i) => (
            <Typography key={i} sx={{ fontWeight: 700 }}>Step {i + 1}: {step}</Typography>
          ))}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
          <Typography sx={{ color: "#1e293b", fontWeight: 800 }}>→</Typography>
          <Typography sx={{ color: "#1e293b", fontWeight: 800, fontSize: "1.25rem" }}>
            {m.answer}
          </Typography>
        </Box>
      </Box>
    );
  }

  // Estimate
  if (card.cardType === "estimate" && card.estimate) {
    const e = card.estimate;
    return (
      <Box sx={cardStyle()} onClick={onClick}>
        {titleEl}
        <Typography sx={{ fontSize: "1.5rem", fontWeight: 800, color: "#7c3aed", mb: 2 }}>≈ {e.calc}</Typography>
        {qaEl(e.question, e.answer)}
      </Box>
    );
  }

  // Logic
  if (card.cardType === "logic" && card.logic) {
    const l = card.logic;
    return (
      <Box sx={cardStyle()} onClick={onClick}>
        {titleEl}
        <Typography sx={{ fontSize: "0.95rem", color: "#475569", mb: 2, fontStyle: "italic" }}>{l.clue}</Typography>
        {qaEl(l.question || l.clue, l.answer)}
      </Box>
    );
  }

  // Inference
  if (card.cardType === "inference" && card.inference) {
    const i = card.inference;
    return (
      <Box sx={cardStyle()} onClick={onClick}>
        {titleEl}
        <Typography sx={{ fontSize: "0.9rem", color: "#475569", mb: 2, fontStyle: "italic" }}>"{i.text}"</Typography>
        {qaEl(i.question, i.answer)}
      </Box>
    );
  }

  // Main idea
  if (card.cardType === "main-idea" && card.mainIdea) {
    const m = card.mainIdea;
    return (
      <Box sx={cardStyle()} onClick={onClick}>
        {titleEl}
        <Typography sx={{ fontSize: "0.9rem", color: "#475569", mb: 2, fontStyle: "italic" }}>"{m.text}"</Typography>
        {qaEl(m.question, m.answer)}
      </Box>
    );
  }

  // Prefix / Suffix
  if (card.cardType === "prefix-suffix" && card.prefixSuffix) {
    const p = card.prefixSuffix;
    return (
      <Box sx={cardStyle()} onClick={onClick}>
        {titleEl}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 2 }}>
          <Box sx={{ px: 1.5, py: 0.5, bgcolor: "#fde047", borderRadius: 2, fontWeight: 700 }}>{p.part}</Box>
          <Typography sx={{ fontWeight: 800 }}>+</Typography>
          <Box sx={{ px: 1.5, py: 0.5, bgcolor: "#dbeafe", borderRadius: 2, fontWeight: 700 }}>{p.word}</Box>
          <Typography sx={{ fontWeight: 800 }}>=</Typography>
          <Box sx={{ px: 1.5, py: 0.5, bgcolor: "#dcfce7", borderRadius: 2, fontWeight: 700 }}>{p.result}</Box>
        </Box>
        {qaEl(p.question, p.answer)}
      </Box>
    );
  }

  // Noun / Verb
  if (card.cardType === "noun-verb" && card.nounVerb) {
    const n = card.nounVerb;
    return (
      <Box sx={cardStyle()} onClick={onClick}>
        {titleEl}
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", justifyContent: "center", mb: 2 }}>
          {n.examples.map((ex, i) => (
            <Box key={i} sx={{ px: 1.5, py: 0.5, bgcolor: "#e0e7ff", borderRadius: 2, fontWeight: 700 }}>{ex}</Box>
          ))}
        </Box>
        {qaEl(n.question, n.answer)}
      </Box>
    );
  }

  // Analogy
  if (card.cardType === "analogy" && card.analogy) {
    const a = card.analogy;
    return (
      <Box sx={cardStyle()} onClick={onClick}>
        {titleEl}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", justifyContent: "center", mb: 2 }}>
          <Box sx={{ px: 1.5, py: 0.5, bgcolor: "#dbeafe", borderRadius: 2, fontWeight: 700 }}>{a.pair1[0]}</Box>
          <Typography>:</Typography>
          <Box sx={{ px: 1.5, py: 0.5, bgcolor: "#dbeafe", borderRadius: 2, fontWeight: 700 }}>{a.pair1[1]}</Box>
          <Typography>::</Typography>
          <Box sx={{ px: 1.5, py: 0.5, bgcolor: "#dbeafe", borderRadius: 2, fontWeight: 700 }}>{a.pair2[0]}</Box>
          <Typography>:</Typography>
          <Box sx={{ px: 1.5, py: 0.5, bgcolor: "#fde047", borderRadius: 2, fontWeight: 700 }}>{a.pair2[1]}</Box>
        </Box>
        {qaEl(a.question, a.answer)}
      </Box>
    );
  }

  // Homonym
  if (card.cardType === "homonym" && card.homonym) {
    const h = card.homonym;
    return (
      <Box sx={cardStyle()} onClick={onClick}>
        {titleEl}
        <Box sx={{ fontSize: "2rem", fontWeight: 800, mb: 2 }}>{h.word}</Box>
        <Typography sx={{ fontSize: "0.85rem", color: "#475569", mb: 1 }}>1. {h.meaning1}</Typography>
        <Typography sx={{ fontSize: "0.85rem", color: "#475569", mb: 2 }}>2. {h.meaning2}</Typography>
        {qaEl(h.question, h.answer)}
      </Box>
    );
  }

  // Letter sequence
  if (card.cardType === "letter-sequence" && card.letterSequence) {
    const ls = card.letterSequence;
    return (
      <Box sx={cardStyle()} onClick={onClick}>
        {titleEl}
        <Box sx={{ display: "flex", gap: 0.5, mb: 2, fontSize: "1.5rem", fontWeight: 800 }}>
          {ls.letters.map((l, i) => <span key={i}>{l}</span>)}
          <Typography component="span">→ ?</Typography>
        </Box>
        {qaEl(ls.question, ls.answer)}
      </Box>
    );
  }

  // Code shift
  if (card.cardType === "code-shift" && card.codeShift) {
    const c = card.codeShift;
    return (
      <Box sx={cardStyle()} onClick={onClick}>
        {titleEl}
        <Typography sx={{ fontSize: "1.3rem", fontWeight: 800, fontFamily: "monospace", mb: 2 }}>{c.encoded}</Typography>
        <Typography sx={{ fontSize: "0.85rem", color: "#475569", mb: 1 }}>Shift: {c.shift}</Typography>
        {qaEl(c.question, c.answer)}
      </Box>
    );
  }

  // Letter code
  if (card.cardType === "letter-code" && card.letterCode) {
    const lc = card.letterCode;
    return (
      <Box sx={cardStyle()} onClick={onClick}>
        {titleEl}
        <Box sx={{ display: "flex", gap: 0.5, mb: 2 }}>
          {lc.word.split("").map((c, i) => (
            <Box key={i} sx={{ px: 1.5, py: 0.5, bgcolor: "#e0e7ff", borderRadius: 2, fontWeight: 700 }}>{c}={lc.values[i]}</Box>
          ))}
        </Box>
        {qaEl(lc.question, lc.answer)}
      </Box>
    );
  }

  // Rule table
  if (card.cardType === "rule-table" && card.ruleTable) {
    const r = card.ruleTable;
    return (
      <Box sx={cardStyle()} onClick={onClick}>
        {titleEl}
        <Typography sx={{ fontSize: "1rem", fontFamily: "monospace", mb: 2 }}>{r.input}</Typography>
        {qaEl(r.question, r.answer)}
      </Box>
    );
  }

  // Fact / Opinion
  if (card.cardType === "fact-opinion" && card.factOpinion) {
    const f = card.factOpinion;
    return (
      <Box sx={cardStyle()} onClick={onClick}>
        {titleEl}
        <Typography sx={{ fontSize: "0.9rem", color: "#475569", mb: 2, fontStyle: "italic" }}>"{f.statement}"</Typography>
        {qaEl(f.question, f.answer)}
      </Box>
    );
  }

  // Shape analogy
  if (card.cardType === "shape-analogy" && card.shapeAnalogy) {
    const sa = card.shapeAnalogy;
    return (
      <Box sx={cardStyle()} onClick={onClick}>
        {titleEl}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, fontSize: "1.5rem" }}>
          <span>{sa.from}</span>
          <Typography>→</Typography>
          <span>{sa.to}</span>
          <Typography sx={{ fontSize: "0.8rem" }}>({sa.change})</Typography>
        </Box>
        {qaEl(sa.question, sa.answer)}
      </Box>
    );
  }

  // Transformation
  if (card.cardType === "transformation" && card.transformation) {
    const t = card.transformation;
    return (
      <Box sx={cardStyle()} onClick={onClick}>
        {titleEl}
        <Typography sx={{ fontSize: "1rem", fontWeight: 700, mb: 2 }}>Type: {t.type}</Typography>
        {qaEl(t.question, t.answer)}
      </Box>
    );
  }

  // Odd one out
  if (card.cardType === "odd-one-out" && card.oddOneOut) {
    const o = card.oddOneOut;
    return (
      <Box sx={cardStyle()} onClick={onClick}>
        {titleEl}
        <Box sx={{ display: "flex", gap: 1, fontSize: "2rem", justifyContent: "center", mb: 2 }}>
          {o.items.map((item, i) => <span key={i}>{item}</span>)}
        </Box>
        {qaEl(o.question, o.answer)}
      </Box>
    );
  }

  // Common feature
  if (card.cardType === "common-feature" && card.commonFeature) {
    const c = card.commonFeature;
    return (
      <Box sx={cardStyle()} onClick={onClick}>
        {titleEl}
        <Box sx={{ display: "flex", gap: 1, fontSize: "2rem", justifyContent: "center", mb: 2 }}>
          {c.shapes.map((s, i) => <span key={i}>{s}</span>)}
        </Box>
        {qaEl(c.question, c.answer)}
      </Box>
    );
  }

  // Rotation / Reflection
  if (card.cardType === "rotation-reflection" && card.rotationReflection) {
    const r = card.rotationReflection;
    return (
      <Box sx={cardStyle()} onClick={onClick}>
        {titleEl}
        {r.shape && <Typography sx={{ fontSize: "2rem", mb: 2 }}>{r.shape}</Typography>}
        {qaEl(r.question, r.answer)}
      </Box>
    );
  }

  // Matrix
  if (card.cardType === "matrix" && card.matrix) {
    const m = card.matrix;
    const flat = Array.isArray(m.grid[0]) ? m.grid.flat() : m.grid;
    return (
      <Box sx={cardStyle()} onClick={onClick}>
        {titleEl}
        <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${m.grid[0]?.length || 2}, 1fr)`, gap: 1, mb: 2 }}>
          {flat.map((cell, i) => (
            <Box key={i} sx={{ width: 40, height: 40, border: "2px solid #94a3b8", borderRadius: 1, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800 }}>{cell}</Box>
          ))}
        </Box>
        {qaEl(m.question, m.answer)}
      </Box>
    );
  }

  // Spatial & 3D reasoning (simple 3D-style visuals)
  if (card.cardType === "spatial-3d" && card.spatial3d) {
    const s3 = card.spatial3d;
    const isNet = s3.kind === "net";
    return (
      <Box sx={cardStyle()} onClick={onClick}>
        {titleEl}
        {isNet ? (
          // Cube / prism net made from squares / rectangles
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 2, gap: 0.5 }}>
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <Box sx={{ width: 36, height: 36, bgcolor: "#e0f2fe", border: "2px solid #0ea5e9" }} />
            </Box>
            <Box sx={{ display: "flex", gap: 0.5 }}>
              {Array(4).fill(null).map((_, i) => (
                <Box key={i} sx={{ width: 36, height: 36, bgcolor: "#dbeafe", border: "2px solid #6366f1" }} />
              ))}
            </Box>
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <Box sx={{ width: 36, height: 36, bgcolor: "#e0f2fe", border: "2px solid #0ea5e9" }} />
            </Box>
          </Box>
        ) : (
          // Stacked cubes with light 3D effect
          <Box sx={{ position: "relative", width: 140, height: 120, mb: 2 }}>
            {[
              { left: 20, top: 40 },
              { left: 60, top: 40 },
              { left: 40, top: 10 },
            ].map((pos, i) => (
              <Box
                key={i}
                sx={{
                  position: "absolute",
                  left: pos.left,
                  top: pos.top,
                  width: 40,
                  height: 40,
                  bgcolor: "#bfdbfe",
                  boxShadow: "8px 8px 0 #93c5fd",
                  borderRadius: 1,
                  border: "2px solid #1d4ed8",
                }}
              />
            ))}
          </Box>
        )}
        {qaEl(s3.question, s3.answer)}
      </Box>
    );
  }

  return null;
}

/** Returns JSX for the back (word description) of a visual card. Returns null if not a visual card. */
function renderVisualCardBack(card, topicLabel) {
  const backBox = (children) => (
    <Box sx={{ p: 4, pt: 5 }}>
      <Typography sx={{ fontSize: "0.8rem", color: "#374151", mb: 1 }}>{topicLabel}</Typography>
      <Typography fontWeight={800} sx={{ color: "#1e293b", textTransform: "uppercase", fontSize: "1.2rem", mb: 2, letterSpacing: 0.5 }}>{card.title}</Typography>
      {children}
    </Box>
  );

  if (card.cardType === "place-value-worksheet" && card.placeValueWorksheet) {
    const ws = card.placeValueWorksheet;
    const renderNumberWithUnderline = (numStr, underlinedDigit) => {
      const chars = numStr.split("");
      return (
        <Typography component="span" sx={{ fontFamily: "monospace", fontSize: "1.1rem", fontWeight: 700 }}>
          {chars.map((ch, i) =>
            ch === underlinedDigit ? (
              <Box key={i} component="span" sx={{ textDecoration: "underline", textUnderlineOffset: 2 }}>{ch}</Box>
            ) : (
              <span key={i}>{ch}</span>
            )
          )}
        </Typography>
      );
    };
    return backBox(
      <>
        <Box sx={{ width: "100%", textAlign: "left" }}>
          {ws.items.map((item, idx) => (
            <Box key={idx} sx={{ mb: 1.5, display: "flex", flexDirection: "column", gap: 0.25 }}>
              <Typography sx={{ color: "#1e293b", fontSize: "1.1rem" }}>
                {item.label}) {renderNumberWithUnderline(item.number, item.underlinedDigit)}
              </Typography>
              <Typography sx={{ color: "#1e293b", fontSize: "1rem", fontWeight: 700 }}>
                Answer: {item.answer}
              </Typography>
            </Box>
          ))}
        </Box>
      </>
    );
  }
  if (card.cardType === "numbers-in-words-worksheet" && card.numbersInWordsWorksheet) {
    const ws = card.numbersInWordsWorksheet;
    return backBox(
      <>
        <Box sx={{ width: "100%", textAlign: "left" }}>
          {ws.items.map((item, idx) => (
            <Box key={idx} sx={{ mb: idx < ws.items.length - 1 ? 0.75 : 0, display: "flex", flexDirection: "column", gap: 0.15 }}>
              <Typography sx={{ color: "#1e293b", fontSize: "1.05rem", fontWeight: 600 }}>
                {item.label}) {item.number}
              </Typography>
              <Typography sx={{ color: "#1e293b", fontSize: "0.95rem", fontWeight: 700 }}>
                Answer: {item.answer}
              </Typography>
            </Box>
          ))}
        </Box>
      </>
    );
  }
  if (card.cardType === "ascending-order-worksheet" && card.ascendingOrderWorksheet) {
    const ws = card.ascendingOrderWorksheet;
    return backBox(
      <>
        <Box sx={{ width: "100%", textAlign: "left" }}>
          {ws.items.map((item, idx) => (
            <Box key={idx} sx={{ mb: idx < ws.items.length - 1 ? 1 : 0, display: "flex", flexDirection: "column", gap: 0.15 }}>
              <Typography sx={{ color: "#1e293b", fontSize: "1.05rem", fontWeight: 600 }}>
                {item.label}) {item.numbers}
              </Typography>
              <Typography sx={{ color: "#1e293b", fontSize: "0.95rem", fontWeight: 700 }}>
                Answer: {item.answer}
              </Typography>
            </Box>
          ))}
        </Box>
      </>
    );
  }
  if (card.cardType === "descending-order-worksheet" && card.descendingOrderWorksheet) {
    const ws = card.descendingOrderWorksheet;
    return backBox(
      <>
        <Box sx={{ width: "100%", textAlign: "left" }}>
          {ws.items.map((item, idx) => (
            <Box key={idx} sx={{ mb: idx < ws.items.length - 1 ? 1 : 0, display: "flex", flexDirection: "column", gap: 0.15 }}>
              <Typography sx={{ color: "#1e293b", fontSize: "1.05rem", fontWeight: 600 }}>
                {item.label}) {item.numbers}
              </Typography>
              <Typography sx={{ color: "#1e293b", fontSize: "0.95rem", fontWeight: 700 }}>
                Answer: {item.answer}
              </Typography>
            </Box>
          ))}
        </Box>
      </>
    );
  }
  if (card.cardType === "comparison-symbol-worksheet" && card.comparisonSymbolWorksheet) {
    const ws = card.comparisonSymbolWorksheet;
    return backBox(
      <>
        <Box sx={{ width: "100%", textAlign: "left" }}>
          {ws.items.map((item, idx) => (
            <Box key={idx} sx={{ mb: idx < ws.items.length - 1 ? 0.75 : 0, display: "flex", flexDirection: "column", gap: 0.15 }}>
              <Typography sx={{ color: "#1e293b", fontSize: "1rem", fontWeight: 600 }}>
                {item.label}) {item.left} <Box component="span" sx={{ color: "#2563eb", fontWeight: 800 }}>{item.answer}</Box> {item.right}
              </Typography>
            </Box>
          ))}
        </Box>
      </>
    );
  }
  if (card.cardType === "round-to-nearest-worksheet" && card.roundToNearestWorksheet) {
    const ws = card.roundToNearestWorksheet;
    return backBox(
      <>
        <Box sx={{ width: "100%", textAlign: "left" }}>
          {ws.items.map((item, idx) => (
            <Box key={idx} sx={{ mb: idx < ws.items.length - 1 ? 0.75 : 0, display: "flex", flexDirection: "column", gap: 0.15 }}>
              <Typography sx={{ color: "#1e293b", fontSize: "1.05rem", fontWeight: 600 }}>
                {item.label}) {item.number}
              </Typography>
              <Typography sx={{ color: "#1e293b", fontSize: "0.95rem", fontWeight: 700 }}>
                Answer: {item.answer}
              </Typography>
            </Box>
          ))}
        </Box>
      </>
    );
  }
  if (card.cardType === "round-to-nearest-hundred-worksheet" && card.roundToNearestHundredWorksheet) {
    const ws = card.roundToNearestHundredWorksheet;
    return backBox(
      <>
        <Box sx={{ width: "100%", textAlign: "left" }}>
          {ws.items.map((item, idx) => (
            <Box key={idx} sx={{ mb: idx < ws.items.length - 1 ? 0.75 : 0, display: "flex", flexDirection: "column", gap: 0.15 }}>
              <Typography sx={{ color: "#1e293b", fontSize: "1.05rem", fontWeight: 600 }}>
                {item.label}) {item.number}
              </Typography>
              <Typography sx={{ color: "#1e293b", fontSize: "0.95rem", fontWeight: 700 }}>
                Answer: {item.answer}
              </Typography>
            </Box>
          ))}
        </Box>
      </>
    );
  }
  if (card.cardType === "fill-missing-numbers-worksheet" && card.fillMissingNumbersWorksheet) {
    const ws = card.fillMissingNumbersWorksheet;
    return backBox(
      <>
        <Box sx={{ width: "100%", textAlign: "left" }}>
          {ws.items.map((item, idx) => {
            const filledEquation = item.equation.replace("___", item.answer);
            return (
              <Box key={idx} sx={{ mb: idx < ws.items.length - 1 ? 1 : 0 }}>
                <Typography sx={{ color: "#1e293b", fontSize: "1.05rem", fontWeight: 600 }}>
                  {item.label}) {filledEquation}
                </Typography>
                <Typography sx={{ color: "#2563eb", fontSize: "0.95rem", fontWeight: 700 }}>
                  Answer: {item.answer}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </>
    );
  }
  if (card.cardType === "number-bonds-worksheet" && card.numberBondsWorksheet) {
    const ws = card.numberBondsWorksheet;
    return backBox(
      <>
        <Box sx={{ width: "100%", textAlign: "left" }}>
          {ws.items.map((item, idx) => {
            const filledEquation = item.equation.replace("___", item.answer);
            return (
              <Box key={idx} sx={{ mb: idx < ws.items.length - 1 ? 1 : 0 }}>
                <Typography sx={{ color: "#1e293b", fontSize: "1.05rem", fontWeight: 600 }}>
                  {item.label}) {filledEquation}
                </Typography>
                <Typography sx={{ color: "#2563eb", fontSize: "0.95rem", fontWeight: 700 }}>
                  Answer: {item.answer}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </>
    );
  }
  if (card.cardType === "number-bonds-pairs-worksheet" && card.numberBondsPairsWorksheet) {
    const ws = card.numberBondsPairsWorksheet;
    return backBox(
      <>
        {ws.example && (
          <Typography sx={{ color: "#374151", fontSize: "0.95rem", mb: 1.5, fontStyle: "italic" }}>
            Example: {ws.example}
          </Typography>
        )}
        <Box sx={{ width: "100%", textAlign: "left" }}>
          {ws.items.map((item, idx) => (
            <Box key={idx} sx={{ mb: idx < ws.items.length - 1 ? 1 : 0 }}>
              <Typography sx={{ color: "#1e293b", fontSize: "1.05rem", fontWeight: 600 }}>
                {item.label}) {item.number} = {item.answer}
              </Typography>
            </Box>
          ))}
        </Box>
      </>
    );
  }
  if (card.cardType === "partition-method-worksheet" && card.partitionMethodWorksheet) {
    const ws = card.partitionMethodWorksheet;
    return backBox(
      <>
        {ws.instruction && (
          <Typography sx={{ color: "#475569", fontSize: "0.9rem", mb: 1.5, textAlign: "left", width: "100%" }}>{ws.instruction}</Typography>
        )}
        <Box sx={{ width: "100%", textAlign: "left" }}>
          {ws.items.map((item, idx) => (
            <Box key={idx} sx={{ mb: idx < ws.items.length - 1 ? 1 : 0, display: "flex", flexDirection: "column", gap: 0.15 }}>
              <Typography sx={{ color: "#1e293b", fontSize: "1.05rem", fontWeight: 600 }}>
                {item.label}) {item.sum}
              </Typography>
              <Typography sx={{ color: "#1e293b", fontSize: "0.95rem", fontWeight: 700 }}>
                Answer: {item.answer}
              </Typography>
            </Box>
          ))}
        </Box>
      </>
    );
  }
  if (card.cardType === "number-line" && card.numberLine) {
    const nl = card.numberLine;
    const drawSteps = nl.drawTip ? nl.drawTip.split(/(?=\d+\))/).map((s) => s.trim()).filter(Boolean) : [];
    return backBox(
      <>
        <Typography sx={{ color: "#475569", fontSize: "1rem", lineHeight: 1.7, mb: 1.5 }}>{nl.readTip}</Typography>
        <Typography sx={{ color: "#1e293b", fontWeight: 700, fontSize: "1rem", mb: 1 }}>How to draw a number line:</Typography>
        {drawSteps.length > 0 ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            {drawSteps.map((step, idx) => (
              <Typography key={idx} sx={{ color: "#475569", fontSize: "1rem", lineHeight: 1.6 }}>
                {step}
              </Typography>
            ))}
          </Box>
        ) : (
          <Typography sx={{ color: "#475569", fontSize: "1rem", lineHeight: 1.7 }}>{nl.drawTip}</Typography>
        )}
      </>
    );
  }
  if (card.cardType === "number-line-sums-worksheet" && card.numberLineSumsWorksheet) {
    const ws = card.numberLineSumsWorksheet;
    const lineWidth = 260;
    const lineTop = 20;
    const tickTop = 22;
    const minLineHeight = 52;
    const renderMiniNumberLine = (startDot, endDot) => (
      <Box sx={{ width: lineWidth, position: "relative", minHeight: minLineHeight, mb: 1 }}>
        <Box sx={{ position: "absolute", left: 0, right: 0, top: lineTop, height: 2, bgcolor: "#1e293b", borderRadius: 1 }} />
        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => {
          const pct = (n / 10) * 100;
          const showStart = startDot !== undefined && n === startDot;
          const showEnd = endDot !== undefined && n === endDot;
          return (
            <Box key={n} sx={{ position: "absolute", left: `${pct}%`, transform: "translateX(-50%)", top: tickTop, display: "flex", flexDirection: "column", alignItems: "center" }}>
              <Box sx={{ width: 2, height: 8, bgcolor: "#475569", borderRadius: 0.5 }} />
              {(showStart || showEnd) && (
                <Box sx={{ position: "absolute", top: -16, width: 14, height: 14, borderRadius: "50%", bgcolor: showEnd ? "#dc2626" : "#2563eb", border: "2px solid", borderColor: showEnd ? "#991b1b" : "#1d4ed8", zIndex: 1 }} />
              )}
              <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: "#1e293b", mt: 0.25 }}>{n}</Typography>
            </Box>
          );
        })}
      </Box>
    );
    const backItemsSx = { width: "100%", textAlign: "left" };
    if (ws.items.length < 4) backItemsSx.minHeight = 280;
    return backBox(
      <>
        <Typography sx={{ color: "#475569", fontSize: "0.9rem", mb: 1.5 }}>{ws.instruction}</Typography>
        <Box sx={backItemsSx}>
          {ws.items.map((item, idx) => (
            <Box key={idx} sx={{ mb: 1.5 }}>
              <Typography sx={{ color: "#1e293b", fontSize: "1rem", fontWeight: 600 }}>{item.label}. {item.sum} = {item.answer}</Typography>
              {renderMiniNumberLine(item.start, item.end)}
            </Box>
          ))}
        </Box>
      </>
    );
  }
  if (card.cardType === "place-value" && card.placeValue) {
    const pv = card.placeValue;
    const hasHundreds = (pv.hundreds ?? 0) > 0;
    const total = (pv.hundreds ?? 0) * 100 + pv.tens * 10 + pv.ones;
    return backBox(
      <>
        <Typography sx={{ color: "#475569", fontSize: "1rem", lineHeight: 1.7, mb: 2 }}>Tells you how much a digit is worth depending on where it sits (ones, tens, hundreds, thousands…)</Typography>
        <Typography sx={{ color: "#475569", fontSize: "1rem", lineHeight: 1.7, mb: 2 }}>The number <strong>{total}</strong> is shown using place value blocks:</Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {hasHundreds && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Box sx={{ width: 32, height: 32, borderRadius: 1, bgcolor: "#059669", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800 }}>{pv.hundreds}</Box>
              <Typography sx={{ color: "#059669", fontSize: "0.9rem", fontWeight: 700 }}>Hundreds (100 each)</Typography>
            </Box>
          )}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box sx={{ width: 32, height: 32, borderRadius: 1, bgcolor: "#2563eb", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800 }}>{pv.tens}</Box>
            <Typography sx={{ color: "#2563eb", fontSize: "0.9rem", fontWeight: 700 }}>Tens (10 each)</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box sx={{ width: 32, height: 32, borderRadius: 1, bgcolor: "#2563eb", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800 }}>{pv.ones}</Box>
            <Typography sx={{ color: "#2563eb", fontSize: "0.9rem", fontWeight: 700 }}>Ones (1 each)</Typography>
          </Box>
        </Box>
        <Typography sx={{ color: "#1e293b", fontWeight: 700, mt: 2 }}>Together: {hasHundreds ? `${pv.hundreds * 100} + ` : ""}{pv.tens * 10} + {pv.ones} = {total}</Typography>
      </>
    );
  }
  if (card.cardType === "arithmetic" && card.arithmetic) {
    const a = card.arithmetic;
    const descMap = { add: "Adding two numbers gives the sum.", subtract: "Taking away one number from another gives the difference.", multiply: "Multiplying two numbers gives the product.", divide: "Dividing one number by another gives the quotient." };
    const desc = descMap[a.op] || card.description;
    return backBox(<Typography sx={{ color: "#475569", fontSize: "1rem", lineHeight: 1.7 }}>{desc} {card.description}</Typography>);
  }
  if (card.cardType === "fraction" && card.fraction) {
    const f = card.fraction;
    return backBox(<Typography sx={{ color: "#475569", fontSize: "1rem", lineHeight: 1.7 }}>The shaded pink blocks show {f.num} parts out of {f.den} equal parts. This is the fraction {f.num}/{f.den}. {card.description}</Typography>);
  }
  if (card.cardType === "decimal" && card.decimal) {
    return backBox(<Typography sx={{ color: "#475569", fontSize: "1rem", lineHeight: 1.7 }}>The dot separates whole numbers from parts of a whole. {card.description}</Typography>);
  }
  if (card.cardType === "percent" && card.percent) {
    const p = card.percent;
    return backBox(
      <Typography sx={{ color: "#475569", fontSize: "1rem", lineHeight: 1.7 }}>
        <span style={{ color: "#059669", fontWeight: 700 }}>{p.value}%</span> means {p.value} out of every 100. {card.description}
      </Typography>
    );
  }
  if (card.cardType === "ratio" && card.ratio) {
    const r = card.ratio;
    return backBox(<Typography sx={{ color: "#475569", fontSize: "1rem", lineHeight: 1.7 }}>The red and blue blocks show the ratio {r.a}:{r.b} – for every {r.a} of one, there are {r.b} of the other. {card.description}</Typography>);
  }
  if (card.cardType === "angle" && card.angle) {
    const types = { right: "90° (like a corner of a square)", acute: "less than 90°", obtuse: "between 90° and 180°" };
    return backBox(<Typography sx={{ color: "#475569", fontSize: "1rem", lineHeight: 1.7 }}>The diagram shows an angle that is {types[card.angle.type] || ""}. {card.description}</Typography>);
  }
  if (card.cardType === "word-pair" && card.wordPair) {
    const w = card.wordPair;
    return backBox(<Typography sx={{ color: "#475569", fontSize: "1rem", lineHeight: 1.7 }}>"{w.word1}" and "{w.word2}" are {w.type === "synonym" ? "synonyms" : "antonyms"}. {card.description}</Typography>);
  }
  if ((card.cardType === "equation" || card.cardType === "expression") && (card.equation || card.expression)) {
    const eq = card.equation || card.expression;
    return backBox(<Typography sx={{ color: "#475569", fontSize: "1rem", lineHeight: 1.7 }}>The symbols and numbers "{eq.expr}" form an {card.cardType === "equation" ? "equation" : "expression"}. {card.description}</Typography>);
  }
  if (card.cardType === "sequence" && card.sequence) {
    const s = card.sequence;
    return backBox(<Typography sx={{ color: "#475569", fontSize: "1rem", lineHeight: 1.7 }}>The pattern {s.terms.join(", ")} follows the rule: {s.rule}. {card.description}</Typography>);
  }
  if (card.cardType === "shape-pattern" && card.shapePattern) {
    return backBox(<Typography sx={{ color: "#475569", fontSize: "1rem", lineHeight: 1.7 }}>The shapes follow a repeating rule. {card.description}</Typography>);
  }
  if ((card.cardType === "perimeter" || card.cardType === "area" || card.cardType === "volume") && (card.perimeter || card.area || card.volume)) {
    return backBox(<Typography sx={{ color: "#475569", fontSize: "1rem", lineHeight: 1.7 }}>{card.description}</Typography>);
  }
  if (card.cardType === "anagram" && card.anagram) {
    return backBox(<Typography sx={{ color: "#475569", fontSize: "1rem", lineHeight: 1.7 }}>Rearranging the letters of "{card.anagram.scrambled}" can make different words. {card.description}</Typography>);
  }
  if (card.cardType === "compound" && card.compound) {
    const c = card.compound;
    return backBox(<Typography sx={{ color: "#475569", fontSize: "1rem", lineHeight: 1.7 }}>"{c.part1}" + "{c.part2}" = "{c.word}". {card.description}</Typography>);
  }
  if (card.cardType === "proportion" && card.proportion) {
    return backBox(<Typography sx={{ color: "#475569", fontSize: "1rem", lineHeight: 1.7 }}>{card.description}</Typography>);
  }
  if (card.cardType === "digit" && card.digit) {
    return backBox(<Typography sx={{ color: "#475569", fontSize: "1rem", lineHeight: 1.7 }}>Digits are the symbols 0, 1, 2, 3, 4, 5, 6, 7, 8, 9. We use them to build all numbers. {card.description}</Typography>);
  }
  if (card.cardType === "rounding" && card.rounding) {
    const r = card.rounding;
    return backBox(<Typography sx={{ color: "#475569", fontSize: "1rem", lineHeight: 1.7 }}>To round to the nearest 10, look at the ones digit. If it is 5 or more, round up. {r.number} rounds to {r.answer}. {card.description}</Typography>);
  }
  if (card.cardType === "sharing-ratio" && card.sharingRatio) {
    const s = card.sharingRatio;
    return backBox(<Typography sx={{ color: "#475569", fontSize: "1rem", lineHeight: 1.7 }}>Splitting {s.total} in ratio {s.ratio[0]}:{s.ratio[1]} means one part gets {s.ratio[0]} and the other gets {s.ratio[1]}. {card.description}</Typography>);
  }
  if (card.cardType === "statistics" && card.statistics) {
    const s = card.statistics;
    const desc = s.type === "mean" ? "Add all values and divide by the count." : s.type === "mode" ? "The value that appears most often." : "Largest minus smallest.";
    return backBox(<Typography sx={{ color: "#475569", fontSize: "1rem", lineHeight: 1.7 }}>The {s.type}: {desc} For {s.values.join(", ")} the answer is {s.answer}. {card.description}</Typography>);
  }
  if (card.cardType === "multi-step" && card.multiStep) {
    return backBox(<Typography sx={{ color: "#475569", fontSize: "1rem", lineHeight: 1.7 }}>Do each step in order. {card.description}</Typography>);
  }
  if (card.cardType === "estimate" && card.estimate) {
    return backBox(<Typography sx={{ color: "#475569", fontSize: "1rem", lineHeight: 1.7 }}>Round numbers to make a quick guess. {card.description}</Typography>);
  }
  if (card.cardType === "logic" && card.logic) {
    return backBox(<Typography sx={{ color: "#475569", fontSize: "1rem", lineHeight: 1.7 }}>Use the clues to work out the answer step by step. {card.description}</Typography>);
  }
  if (card.cardType === "inference" && card.inference) {
    return backBox(<Typography sx={{ color: "#475569", fontSize: "1rem", lineHeight: 1.7 }}>We infer from clues in the text even when it is not written directly. {card.description}</Typography>);
  }
  if (card.cardType === "main-idea" && card.mainIdea) {
    return backBox(<Typography sx={{ color: "#475569", fontSize: "1rem", lineHeight: 1.7 }}>The main idea is what the whole text is mostly about. {card.description}</Typography>);
  }
  if (card.cardType === "prefix-suffix" && card.prefixSuffix) {
    const p = card.prefixSuffix;
    return backBox(<Typography sx={{ color: "#475569", fontSize: "1rem", lineHeight: 1.7 }}>A {p.type} ({p.part}) is added to "{p.word}" to make "{p.result}". {card.description}</Typography>);
  }
  if (card.cardType === "noun-verb" && card.nounVerb) {
    return backBox(<Typography sx={{ color: "#475569", fontSize: "1rem", lineHeight: 1.7 }}>{card.description}</Typography>);
  }
  if (card.cardType === "analogy" && card.analogy) {
    const a = card.analogy;
    return backBox(<Typography sx={{ color: "#475569", fontSize: "1rem", lineHeight: 1.7 }}>"{a.pair1[0]}" and "{a.pair1[1]}" have the same relationship as "{a.pair2[0]}" and "{a.pair2[1]}". {card.description}</Typography>);
  }
  if (card.cardType === "homonym" && card.homonym) {
    const h = card.homonym;
    return backBox(<Typography sx={{ color: "#475569", fontSize: "1rem", lineHeight: 1.7 }}>"{h.word}" can mean "{h.meaning1}" or "{h.meaning2}". Same spelling, same sound, different meanings. {card.description}</Typography>);
  }
  if (card.cardType === "letter-sequence" && card.letterSequence) {
    return backBox(<Typography sx={{ color: "#475569", fontSize: "1rem", lineHeight: 1.7 }}>The letters follow a pattern. {card.description}</Typography>);
  }
  if (card.cardType === "code-shift" && card.codeShift) {
    return backBox(<Typography sx={{ color: "#475569", fontSize: "1rem", lineHeight: 1.7 }}>Each letter is shifted in the alphabet. {card.description}</Typography>);
  }
  if (card.cardType === "letter-code" && card.letterCode) {
    return backBox(<Typography sx={{ color: "#475569", fontSize: "1rem", lineHeight: 1.7 }}>Letters are matched to numbers. Add the values for each letter. {card.description}</Typography>);
  }
  if (card.cardType === "rule-table" && card.ruleTable) {
    return backBox(<Typography sx={{ color: "#475569", fontSize: "1rem", lineHeight: 1.7 }}>{card.description}</Typography>);
  }
  if (card.cardType === "fact-opinion" && card.factOpinion) {
    return backBox(<Typography sx={{ color: "#475569", fontSize: "1rem", lineHeight: 1.7 }}>{card.description}</Typography>);
  }
  if (card.cardType === "shape-analogy" || card.cardType === "transformation" || card.cardType === "odd-one-out" || card.cardType === "common-feature" || card.cardType === "rotation-reflection" || card.cardType === "matrix") {
    return backBox(<Typography sx={{ color: "#475569", fontSize: "1rem", lineHeight: 1.7 }}>{card.description}</Typography>);
  }
  return null;
}

export { renderVisualCard, renderVisualCardBack };
